import Controller from '@ember/controller';
import { action } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task, timeout } from 'ember-concurrency';
import type { Hotel } from 'frontend-utils/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { MI_UPSELL_CONTACT_LOCALSTORAGE_KEY } from 'lumen/utils/constants/general-constants';

interface ContactRequest {
  subscriptionId: string;
  user: string;
}

export default class HotelMarketInsightUpsellController extends Controller {
  @service declare access: Services['access'];
  @service declare ajax: Services['ajax'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare localStorage: Services['local-storage'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  @tracked isLoading = false;
  @tracked isTrialRequestedInCurrentSession = false;

  MODAL_VIEWS = {
    REQUEST_TRIAL: 1,
    AWAITING_TRIAL_SETUP: 2,
    TRIAL_ENDED: 3,
  };

  poll = false;
  videoId = 'p1twh1rgad';

  pollMarketInsightStatusTask = task(
    { keepLatest: true },
    async (): Promise<void> => {
      if (this.poll) {
        // Fetch latest subscription status
        await this.store.query<Hotel>('hotel', {
          id: this.hotel.id,
        });

        if (this.access.hasFeature('app.market_insight')) {
          // Redirect to MI if the setup is complete
          this.router.transitionTo('hotel.market-insight');
        } else {
          // Schedule next poll
          await timeout(this.pollInterval);
          void this.pollMarketInsightStatusTask.perform();
        }
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get activeModalView() {
    if (
      this.hotel?.productState?.market_insight?.subscription_status ===
      'inactive'
    ) {
      return this.MODAL_VIEWS.TRIAL_ENDED;
    }
    if (
      this.isTrialRequestedInCurrentSession ||
      this.isTrialRequestedForSubscription(this.hotel.id!) ||
      'market_insight' in this.hotel.productState
    ) {
      return this.MODAL_VIEWS.AWAITING_TRIAL_SETUP;
    }

    return this.MODAL_VIEWS.REQUEST_TRIAL;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    return this.hotelAndMarketService.hotel;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMarketInsightFull() {
    return this.access.hasFeature('app.market_insight_upsell.full');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isQuickTrialSetup() {
    return this.hotelAndMarketService.hotel?.hotelinfo?.smartCompsetGenerated;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pollInterval() {
    if (this.isQuickTrialSetup) {
      return 10 * 1000; // Poll every 10 seconds
    }

    return 10 * 60 * 1000; // Poll every 10 minutes
  }

  handleRequestFailure(): void {
    this.isLoading = false;
    void this.alerting.showErrorAlert();
    void this.errorTracking.captureException(
      'Market Insight trial creation request responded with a non-success status code',
    );
  }

  handleRequestSuccess(): void {
    this.storeTrialRequestInLocalStorage();
    this.isTrialRequestedInCurrentSession = true;
    this.isLoading = false;
    this.startPolling();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  isTrialRequestedForSubscription(subscriptionId: string) {
    try {
      if (this.localStorage.getItem(MI_UPSELL_CONTACT_LOCALSTORAGE_KEY)) {
        const storedContactRequests = JSON.parse(
          this.localStorage.getItem(MI_UPSELL_CONTACT_LOCALSTORAGE_KEY) ?? '[]',
        ) as ContactRequest[];

        // Check if we can find an object containing the current subscription id. This means the request has already been submitted for the current hotel.
        return storedContactRequests.some(
          (contactRequest) => contactRequest.subscriptionId === subscriptionId,
        );
      }
      return false;
    } catch (error) {
      this.errorTracking.captureException(error);
      return false;
    }
  }

  startPolling(): void {
    this.poll = true;
    void this.pollMarketInsightStatusTask.perform();
  }

  stopPolling(): void {
    this.poll = false;
    void this.pollMarketInsightStatusTask.cancelAll();
  }

  storeTrialRequestInLocalStorage(): void {
    if (this.localStorage.getItem(MI_UPSELL_CONTACT_LOCALSTORAGE_KEY)) {
      // add the current subscription if it's not found in localStorage
      if (!this.isTrialRequestedForSubscription(this.hotel.id!)) {
        const storedContactRequests = JSON.parse(
          this.localStorage.getItem(MI_UPSELL_CONTACT_LOCALSTORAGE_KEY) ?? '[]',
        ) as ContactRequest[];

        storedContactRequests.push({
          user: this.hotelAndMarketService.user.email,
          subscriptionId: this.hotel.id!,
        });

        this.localStorage.setItem(
          MI_UPSELL_CONTACT_LOCALSTORAGE_KEY,
          JSON.stringify(storedContactRequests),
        );
      }
    } else {
      // add a stringified array containing the current subscription in localStorage
      this.localStorage.setItem(
        MI_UPSELL_CONTACT_LOCALSTORAGE_KEY,
        JSON.stringify([
          {
            user: this.hotelAndMarketService.user.email,
            subscriptionId: this.hotel.id,
          },
        ]),
      );
    }
  }

  @action redirectToEnabledTab(): void {
    this.transition.redirectToEnabledTab();
  }

  @action requestTrial(): void {
    this.isLoading = true;

    this.ajax
      .request('self-service/marketinsighttrial/create/', {
        method: 'POST',
        contentType: 'application/json',
        data: JSON.stringify({
          subscription_id: parseInt(this.hotel.id!, 10),
        }),
      })
      .then(
        () => {
          this.handleRequestSuccess();
        },
        (r: Response) => {
          if (r.status === 400) {
            // If the trial has already been requested by another user in this session, fake success
            this.handleRequestSuccess();
          } else {
            this.handleRequestFailure();
          }
        },
      );
  }
}
